<template>
  <div class="quote-details__wrapper">
    <div class="quote-table quote-table--white">
      <div :class="quoteTableTitleClass">
        <div>
          <div :class="quoteOrganisationNameClass">
            {{ quote.organisation.name }}
          </div>
          <div v-if="!isCatalogVersionV2">
            <span class="quote-table__subtitle__left">
              {{ $t('quote_details.commitment.label') }}
            </span>
            <span class="quote-table__subtitle__left text--black">
              {{ $t(`quote_details.commitment.${quoteCommitment}`) }}
            </span>

            <span class="quote-table__subtitle__left billing-cycle__text">
              {{ $t('quote_details.billing_cycle.label') }}
            </span>
            <span class="quote-table__subtitle__left text--black">
              {{ $t(`quote_details.billing_cycle.${quoteBillingCycle}`) }}
            </span>
          </div>
        </div>
        <div>
          <span class="quote-table__title__right">
            {{ $t('quote_details.quote_number') }}
          </span>
          <span class="quote-table__title__right text--black">
            #{{ quote.quote_number }}
          </span>
        </div>
      </div>
      <div v-if="isCatalogVersionV2">
        <table
          v-if="showMonthlyTable"
          class="quote-table__details quote-table__details--no-spacing"
        >
          <QuoteDetailBlock
            v-for="shop in quote.organisation.shops"
            :key="shop.id"
            :quote="quote"
            :shop="shop"
            :quote-line-items="quoteLineItemsForShop(shop.id)"
            :is-enterprise-customer="isEnterpriseCustomer"
            with-commitment-details
            with-total
          />
          <div class="quote-table__details__totals">
            <span>
              {{ $t('quote_details.totals.total_amount_subscription_without_tax') }}
            </span>
            <span>
              {{ totalAmountSubscriptionWithoutTax }}
            </span>
          </div>
          <QuoteDetailBlock
            v-if="showFirstMonthTable"
            :quote="quote"
            scope-name="Services"
            :quote-line-items="chargeQuoteLineItems"
          />
          <div
            v-if="showFirstMonthTable"
            class="quote-table__details__totals"
          >
            <span>
              {{ $t('quote_details.totals.total_amount_services_without_tax') }}
            </span>
            <span>
              {{ totalAmountServicesWithoutTax }}
            </span>
          </div>
          <div class="separator" />
          <div class="quote-table__totals-content quote-table__content-padding">
            <div class="quote-table__totals">
              <div class="quote-table__total-line">
                <div class="quote-table__total-title">
                  {{ monthlyQuote.vatLine.name }}
                </div>
                <div class="quote-table__total-value">
                  {{ monthlyQuote.vatLine.price }}
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="showFirstMonthTable"
            class="quote-table__content-padding"
          >
            <div class="quote-table__totals">
              <div class="quote-table__total-line">
                <div class="quote-table__total-title">
                  {{ firstMonthQuote.vatLine.name }}
                </div>
                <div class="quote-table__total-value">
                  {{ firstMonthQuote.vatLine.price }}
                </div>
              </div>
            </div>
          </div>
          <div class="quote-table__total-line__final-price quote-table__final-price__placement">
            <div class="quote-table__total-title">
              {{ firstMonthQuote.finalPrice.name }}
            </div>
            <div class="quote-table__total-value">
              {{ firstMonthQuote.finalPrice.price }}
            </div>
          </div>
        </table>
      </div>
      <div
        v-else
        class="quote-table__content"
      >
        <table
          v-if="showMonthlyTable"
          class="quote-table__details quote-table__details--first-element"
        >
          <thead class="quote-table__details__headers">
            <tr class="quote-table__row quote-table__row--header">
              <th class="quote-table__header quote-table__header--left">
                {{ $t('quote_details.headers.description') }}
              </th>
              <th class="quote-table__header">
                {{ $t('quote_details.headers.quantity') }}
              </th>
              <th class="quote-table__header">
                {{ $t('quote_details.headers.unit_price') }}
              </th>
              <th class="quote-table__header">
                {{ $t('quote_details.headers.total') }}
              </th>
            </tr>
          </thead>
          <colgroup>
            <col
              v-if="isCatalogVersionV2"
              span="1"
            >
            <col
              span="1"
            >
            <col
              span="1"
            >
            <col
              span="1"
            >
          </colgroup>
          <tbody class="quote-table__details__content">
            <template
              v-for="line, index in monthlyQuote.lines"
            >
              <CollapseLine
                :key="index"
                :line="line"
              />
            </template>
          </tbody>
        </table>
        <div class="separator" />
        <div
          v-if="showFirstMonthTable"
          class="quote-table__title__first-month"
        >
          <div class="quote-table__title__first-month__left">
            {{ $t('quote_details.first_month_title') }}
          </div>
          <div>
            <table
              class="quote-table__details"
            >
              <colgroup>
                <col
                  span="1"
                >
                <col
                  span="1"
                >
                <col
                  span="1"
                >
              </colgroup>
              <tbody class="quote-table__details__content">
                <tr
                  v-for="line, index in firstMonthQuote.lines"
                  :key="index"
                  class="quote-table__row"
                >
                  <td class="quote-table__cell quote-table__cell--item-name">
                    {{ line.name }}
                  </td>
                  <td class="quote-table__cell" />
                  <td class="quote-table__cell">
                    {{ line.quantity }}
                  </td>
                  <td class="quote-table__cell">
                    {{ line.unitPrice }}
                  </td>
                  <td class="quote-table__cell">
                    {{ line.totalPrice }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="quote-table__totals-content">
          <div class="quote-table__totals">
            <div
              v-for="line, index in monthlyQuote.totals"
              :key="index"
              class="quote-table__total-line"
            >
              <div class="quote-table__total-title">
                {{ line.name }}
              </div>
              <div class="quote-table__total-value">
                {{ line.price }}
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="showFirstMonthTable"
          class="quote-table__totals-content"
        >
          <div class="quote-table__totals">
            <div
              v-for="line, index in firstMonthQuote.totals"
              :key="index"
              class="quote-table__total-line"
            >
              <div class="quote-table__total-title">
                {{ line.name }}
              </div>
              <div class="quote-table__total-value">
                {{ line.price }}
              </div>
            </div>
          </div>
        </div>
        <div class="quote-table__total-line__final-price">
          <div class="quote-table__total-title">
            {{ monthlyQuote.finalPrice.name }}
          </div>
          <div class="quote-table__total-value">
            {{ monthlyQuote.finalPrice.price }}
          </div>
        </div>
        <div
          v-if="showFirstMonthTable"
          class="quote-table__total-line__final-price__first-month"
        >
          <div class="quote-table__total-title">
            {{ firstMonthQuote.finalPrice.name }}
          </div>
          <div class="quote-table__total-value">
            {{ firstMonthQuote.finalPrice.price }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  mapGetters,
  mapState,
} from 'vuex';

import CollapseLine from '@components/CollapseLine';
import QuoteDetailBlock from '@components/QuoteDetailBlock';
import {
  extractShopsNames, extractPrice,
} from '@/shared/utils/method_helper';

export default {
  components: {
    CollapseLine,
    QuoteDetailBlock,
  },
  computed: {
    ...mapState(['quote']),
    ...mapGetters(['isContractChange', 'isCatalogVersionV2', 'isEnterpriseCustomer']),

    // There is no simple way to mark a quote as yearly or monthly
    // so we return 'yearly' if we can find an item in the quote that needs to be paid yearly
    // else, return monthly by default
    quoteBillingCycle() {
      if (this.isCatalogVersionV2) {
        const firstShopPaymentFrequency =
          this.quote.organisation.shops[0].contract_terms.payment_frequency;
        const uniqPaymentFrequency =
          this.quote.organisation.shops.every(
            shop => shop.contract_terms.payment_frequency === firstShopPaymentFrequency,
          );

        return uniqPaymentFrequency ? firstShopPaymentFrequency.toLowerCase() : 'multiple';
      }

      return this.quote.quote_line_items.find(item => item.type !== 'Charge').billing_cycle_unit;
    },
    quoteCommitment() {
      if (this.isCatalogVersionV2) {
        const firstShopCommitment = this.quote.organisation.shops[0].contract_terms.commitment;
        const uniqCommitment =
          this.quote.organisation.shops.every(
            shop => shop.contract_terms.commitment === firstShopCommitment,
          );

        return uniqCommitment ? firstShopCommitment.toLowerCase().split(' ').join('_') : 'multiple';
      }

      return this.quote.contract_terms.total_engagement_unit.toLowerCase();
    },
    showMonthlyTable() {
      // monthlyQuote won't have lines defined with isCatalogVersionV2
      // due to the alteration of the quote content
      if (this.isCatalogVersionV2) {
        return this.filterQuoteLine(lineItem => lineItem.type !== 'Charge').length > 0;
      }

      return this.monthlyQuote.lines.length > 0;
    },
    showFirstMonthTable() {
      // firstMonthQuote won't have lines defined with isCatalogVersionV2
      // due to the alteration of the quote content
      if (this.isCatalogVersionV2) {
        return this.chargeQuoteLineItems.length > 0;
      }

      return this.firstMonthQuote.lines.length > 0;
    },
    chargeQuoteLineItems() {
      return this.filterQuoteLine(lineItem => lineItem.type === 'Charge');
    },
    totalAmountSubscriptionWithoutTax() {
      return extractPrice(
        this.quote,
        this.quote.quote_line_items
          .filter(lineItem => lineItem.type !== 'Charge')
          .reduce((acc, item) => acc + item.total_amount_with_discount, 0),
      );
    },
    totalAmountServicesWithoutTax() {
      return extractPrice(
        this.quote,
        this.quote.quote_line_items
          .filter(lineItem => lineItem.type === 'Charge')
          .reduce((acc, item) => acc + item.total_amount_with_discount, 0),
      );
    },
    monthlyQuote() {
      const vatLineText = this.isCatalogVersionV2 ?
        this.$t('quote_details.totals.total_tax_v2') :
        this.$t('quote_details.totals.total_tax');

      const vatLine = {
        name: vatLineText,
        subtext: '',
        price: extractPrice(this.quote, this.quote.totals.total_tax),
      };

      const totals = [
        {
          name: this.$t('quote_details.totals.total_amount'),
          subtext: this.$t('quote_details.totals.ex_vat'),
          price: extractPrice(this.quote, this.quote.totals.total_amount_with_discount),
        },
        vatLine,
      ];

      const result = {
        totals,
        vatLine,
        finalPrice: {
          name: this.$t('quote_details.totals.total_amount_with_tax'),
          price: extractPrice(this.quote, this.quote.totals.total_amount_with_tax),
        },
      };

      if (this.isCatalogVersionV2) return result;

      // This will alter quantities we deal with contrary to the initial state of the quote
      const monthlyItems = this.filterQuoteLine(lineItem => lineItem.type !== 'Charge');
      const monthlyLines = this.serializeLines(monthlyItems);

      return {
        ...result,
        lines: monthlyLines,
      };
    },
    firstMonthQuote() {
      const vatLineText = this.isCatalogVersionV2 ?
        this.$t('quote_details.totals.total_tax_services') :
        this.$t('quote_details.totals.total_tax_first_payment');

      const vatLineField = this.isCatalogVersionV2 ?
        this.quote.totals.total_tax_charges :
        this.quote.totals.total_tax_first_payment;

      const vatLine = {
        name: vatLineText,
        subtext: '',
        price: extractPrice(this.quote, vatLineField),
      };

      const totals = [
        {
          name: this.$t('quote_details.totals.total_amount_first_payment'),
          subtext: this.$t('quote_details.totals.ex_vat'),
          price: extractPrice(this.quote, this.quote.totals.total_amount_first_payment),
        },
        vatLine,
      ];

      const finalPriceText = this.isCatalogVersionV2 ?
        this.$t('quote_details.totals.total_amount_with_tax_v2') :
        this.$t('quote_details.totals.total_amount_first_payment_with_tax');

      const result = {
        totals,
        vatLine,
        finalPrice: {
          name: finalPriceText,
          price: extractPrice(this.quote, this.quote.totals.total_amount_first_payment_with_tax),
        },
      };

      if (this.isCatalogVersionV2) return result;

      // This will alter quantities we deal with contrary to the initial state of the quote
      const firstMonthItems = this.filterQuoteLine(lineItem => lineItem.type === 'Charge');
      const firstMonthLines = this.serializeLines(firstMonthItems);

      return {
        ...result,
        lines: firstMonthLines,
      };
    },
    quoteTableTitleClass() {
      return {
        'quote-table__title': true,
        'quote-table__title__v2': this.isCatalogVersionV2,
      };
    },
    quoteOrganisationNameClass() {
      return {
        'quote-table__title__left': true,
        'quote-table__title__orga-name': true,
        'quote-table__title__left__v2': this.isCatalogVersionV2,
      };
    },
  },
  methods: {
    filterQuoteLine(filterCallback) {
      return this.quote.quote_line_items.filter(
        this.isContractChange ?
          lineItem => filterCallback(lineItem) && lineItem.status === 'Added' :
          filterCallback,
      );
    },
    serializeLines(lineItems) {
      const consolidatedLineItems = {};

      lineItems.forEach(lineItem => {
        const existingLineItem = consolidatedLineItems[`${lineItem.title}--${lineItem.total_amount_with_discount}`];

        if (existingLineItem) {
          existingLineItem.quantity += lineItem.quantity;
          existingLineItem.total_amount_with_discount += lineItem.total_amount_with_discount;
          existingLineItem.quote_line_item_shops.push(...lineItem.quote_line_item_shops);
        } else {
          consolidatedLineItems[`${lineItem.title}--${lineItem.total_amount_with_discount}`] = lineItem;
        }
      });

      return Object.values(consolidatedLineItems).map(lineItem => ({
        name: lineItem.title,
        maxUsers: lineItem.max_users,
        quantity: lineItem.quantity,
        unitPrice: extractPrice(this.quote, lineItem.unit_amount),
        totalPrice: extractPrice(this.quote, lineItem.total_amount_with_discount),
        shopsNames: extractShopsNames(lineItem, this.quote),
      }));
    },
    quoteLineItemsForShop(shopId) {
      return this.quote.quote_line_items.filter(
        lineItem => lineItem.quote_line_item_shops[0].shop_id === shopId && lineItem.type !== 'Charge',
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.quote-table  {
  border-radius: 8px;
  margin-bottom: 24px;
}

.quote-table--white {
  border: 1px solid $sk-grey-10;
}

.quote-table--grey {
  background: rgba(217, 217, 217, .2);
}

.quote-table__title {
  border-bottom: 1px solid $sk-grey-10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 24px;

  &__v2 {
    padding: 24px !important;
  }
}

.quote-table__title__first-month {
  border-bottom: 1px solid $sk-grey-10;
}

.quote-table__title__first-month__left {
  padding: 24px 0px;
  font-size: $fs-text-l;
  font-weight: $fw-semi-bold;
}

.quote-table__title__left {
  line-height: 22px;
  font-size: $fs-heading-s;
  font-weight: $fw-semi-bold;

  &__v2 {
    font-size: $fs-heading-m;
  }
}

.quote-table__title__right {
  color: $sk-grey-50;
}

.quote-table__title__orga-name {
  margin-bottom: 4px;
}

.quote-table__subtitle__left {
  font-size: $fs-text-m;
  font-weight: $fw-regular;
  color: $sk-grey-50;
}

.billing-cycle__text {
  margin-left: 12px;
}

.text--black {
  color: $sk-black;
}

.quote-table--grey .quote-table__title__left {
  font-size: 16px;
}

.quote-table__content {
  padding: 0px 24px 24px;
}

.quote-table__content-padding {
  padding: 0 16px;
}

.quote-table__final-price__placement {
  padding: 4px 16px 0;
  // used to override quote-table__total-line__final-price
  // as long as we keep catalog v2 and old version
  margin-top: 0 !important;
}

.quote-table__details {
  table-layout: fixed;
  width: 100%;
  text-align: right;
  margin-bottom: 16px;

  &--first-element {
    padding-top: 24px;
  }

  &--no-spacing {
    border-spacing: 0;
  }
}

.quote-table__details__totals {
  display: flex;
  justify-content: space-between;
  padding: 8px 16px 16px;
  font-size: $fs-heading-s;
  font-weight: $fw-semi-bold;
}

.quote-table__header  {
  font-size: $fs-text-s;
  font-weight: $fw-medium;
  color: $sk-grey-50;
  padding-bottom: 24px;
}

.quote-table__header--left {
  text-align: left;
  color: $sk-grey-50;
  font-size: $fs-text-s;
}

.quote-table__cell {
  line-height: 16px;
  padding: 0px 0px 12px 0px;
}

.quote-table__cell--item-name {
  text-align: left;
}

.quote-table__totals-content {
  padding-top: 16px;
}

.quote-table__totals {
  line-height: 15px;
  font-size: $fs-text-m;
  color: $sk-grey-50;
}

.quote-table__total-line {
  display: flex;
  justify-content: space-between;
}

.quote-table__total-line:nth-of-type(1) {
  margin-bottom: 4px;
}

.quote-table__total__subtext {
  color: $sk-grey-50;
}

.quote-table__total-line__final-price {
  margin-top: 24px;
  line-height: 22px;
  font-weight: $fw-semi-bold;
  font-size: $fs-heading-s;
  display: flex;
  justify-content: space-between;
}

.quote-table__total-line__final-price__first-month {
  margin-top: 12px;
  line-height: 18px;
  font-size: $fs-text-l;
  display: flex;
  justify-content: space-between;
}

.separator {
  border-bottom: 1px solid $sk-grey-10;
}

col {
  width: 15%;

  &:first-child {
    width: 40%;
  }
}
</style>

<style lang="scss">
.collapse-line:not(:last-of-type) > .collapse-line__cell {
  padding-bottom: 12px;
}
</style>
